import { useBusy } from "@/composables/useBusyAction";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import {
  deviceCountsByGroup,
  deviceGroupErrorMessage,
  suggestGroupsFromAddress,
  type AddressSuggestion,
} from "@/dashboard/production_strategy/device_groups/deviceGroupUtils";
import type { DeviceGroupView } from "@/dashboard/production_strategy/device_groups/deviceGroupsTypes";
import { useDeviceGroupsStore } from "@/dashboard/production_strategy/device_groups/useDeviceGroupsStore";
import type { RegistryDevice } from "@/types";
import { useLocalStorage } from "@vueuse/core";
import { computed, ref, toValue, watch, type MaybeRefOrGetter } from "vue";
import { useI18n } from "vue-i18n";

/**
 * The Devices tab's device group state for one company: its groups, which chip is viewed, the
 * table selection and the changes made to it. Switching the view clears the selection, switching the
 * company also returns to every device, and a viewed group that disappears (deleted here or in
 * another tab) falls back to every device. A refused
 * change shows as a warning; the selection is kept so the user can adjust it.
 *
 * @param devices - The devices the tab shows, i.e. without the company's excluded ones
 */
export function useDeviceGroupWorkspace(
  companyId: MaybeRefOrGetter<number>,
  devices: MaybeRefOrGetter<RegistryDevice[]>
) {
  const { t } = useI18n();
  const { globalErrorHandler, globalWarningHandler } = useGlobalHandlers();
  const store = useDeviceGroupsStore();
  const { busy, run } = useBusy();

  const view = ref<DeviceGroupView>("all");
  const selectedIds = ref<number[]>([]);
  // A per-viewer convenience: without storage a dismissed suggestion returns on the next load.
  const dismissedNames = useLocalStorage<string[]>(
    () => `deviceGroups.dismissedSuggestions.${toValue(companyId)}`,
    [],
    { writeDefaults: false }
  );

  const groups = computed(() => store.groupsFor(toValue(companyId)));
  const hasGroups = computed(() => groups.value.length > 0);
  const viewedGroup = computed(() => {
    const viewed = view.value;
    return typeof viewed === "number"
      ? (groups.value.find(({ id }) => id === viewed) ?? null)
      : null;
  });

  /** The devices the viewed chip shows. */
  const devicesInView = computed(() => {
    const all = toValue(devices);
    if (view.value === "all") return all;
    const groupId = view.value === "ungrouped" ? null : view.value;
    return all.filter((device) => device.device_group_id === groupId);
  });

  /** Each group's members among the tab's devices, for the group chips and the "Add to group" menu. */
  const countByGroup = computed(() => deviceCountsByGroup(toValue(devices)));

  const selectedDevices = computed(() => {
    const ids = new Set(selectedIds.value);
    return toValue(devices).filter((device) => ids.has(device.id));
  });

  /** Address suggestions for ungrouped devices, largest first, without the dismissed ones. */
  const suggestions = computed(() =>
    suggestGroupsFromAddress(
      toValue(devices),
      groups.value.map((group) => group.name)
    ).filter((suggestion) => !dismissedNames.value.includes(suggestion.name))
  );

  watch(
    () => toValue(companyId),
    async (id) => {
      try {
        await store.fetchGroups(id);
      } catch (error) {
        globalErrorHandler(error);
      }
    },
    { immediate: true }
  );

  watch(view, () => {
    selectedIds.value = [];
  });

  // The tab is reused across companies; the previous company's selection would be refused whole.
  watch(
    () => toValue(companyId),
    () => {
      view.value = "all";
      selectedIds.value = [];
    }
  );

  watch(viewedGroup, (group) => {
    if (group === null && typeof view.value === "number") view.value = "all";
  });

  const change = (save: () => Promise<void>) =>
    run(async () => {
      try {
        await save();
        selectedIds.value = [];
      } catch (error) {
        const message = deviceGroupErrorMessage(error, t);
        if (message === null) globalErrorHandler(error);
        else globalWarningHandler(message, "");
      }
    });

  const addSelection = (groupId: number) =>
    change(() =>
      store.update(toValue(companyId), groupId, {
        add_device_ids: selectedIds.value,
      })
    );

  const removeSelection = (groupId: number) =>
    change(() =>
      store.update(toValue(companyId), groupId, {
        remove_device_ids: selectedIds.value,
      })
    );

  const createFromSuggestion = (suggestion: AddressSuggestion) =>
    change(async () => {
      const group = await store.create(toValue(companyId), {
        name: suggestion.name,
        device_ids: suggestion.deviceIds,
      });
      view.value = group.id;
    });

  const dismiss = (suggestion: AddressSuggestion) => {
    dismissedNames.value = [...dismissedNames.value, suggestion.name];
  };

  return {
    groups,
    hasGroups,
    view,
    viewedGroup,
    devicesInView,
    selectedIds,
    selectedDevices,
    countByGroup,
    busy,
    suggestions,
    addSelection,
    removeSelection,
    createFromSuggestion,
    dismiss,
  };
}
